import styles from './FilterSearch.module.scss';
import { filtersAPI } from 'api/filtersAPI';
import dropdownArrow from 'assets/images/dropdown-arrow-icon.svg';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectFiltersDisplayMapping,
    selectFiltersRmAllValue,
    selectFiltersTeamAllValue,
    selectMarketValue,
    selectTimeslicerEndMonth,
    selectTimeslicerStartMonth,
    selectZoneValue
} from 'selectors/selectors';
import {
    fetchFilteredDropdownValues,
    updateFilter,
    updateFiltersWhenAllIsSelectedForTeamsOrRms
} from 'slices/FiltersSlice';
import { FilterType } from 'types/types';
import { generateRequestParamsForFilterApi } from 'utils/api';
import { processFilterOptions } from 'utils/filters';
import { isTypeNumber } from 'utils/common';
import { resetScenarioOneCharts } from 'slices/ScenarioOneSlice';

const FilterSearch = (props) => {
    const {
        style,
        filterName,
        filterType,
        searchFieldValue,
        setSearchFieldValue,
        searchFieldLength,
        setSearchFieldLength,
        showOptions,
        setShowOptions,
        isInFocus,
        setIsInFocus,
        label,
        value,
        options,
        onFiltersChanged,
        resetFilters,
        showFilterName = true
    } = props;
    const dispatch = useDispatch();

    // Selectors
    const tsStart = useSelector(selectTimeslicerStartMonth);
    const tsEnd = useSelector(selectTimeslicerEndMonth);
    const zoneValue = useSelector(selectZoneValue);
    const marketValue = useSelector(selectMarketValue);
    const teamAllValue = useSelector(selectFiltersTeamAllValue);
    const rmAllValue = useSelector(selectFiltersRmAllValue);
    const filtersDisplayMapping = useSelector(selectFiltersDisplayMapping);

    // Variables
    const isSelectedFilterTypeTeamOrRm =
        filterType === FilterType.TEAM || filterType === FilterType.RM;

    // Component helper functions
    const handleOptionOnClick = (e, option) => {
        e.preventDefault();
        dispatch(
            updateFilter({
                value: option.value,
                label: option.label,
                filterType
            })
        );
        dispatch(resetScenarioOneCharts());

        if (value !== option.value && label !== option.label) {
            const requestParamsObj = generateRequestParamsForFilterApi(
                tsStart ? tsStart.epoch : null,
                tsEnd ? tsEnd.epoch : null,
                isTypeNumber(zoneValue) && filterType === FilterType.ZONE
                    ? option.value
                    : zoneValue,
                filterType === FilterType.MARKET ? option.value : null,
                filterType === FilterType.TEAM ? option.value : null,
                filterType === FilterType.RM ? option.value : null
            );
            const enrichedParamsObj = {
                requestParams: {
                    ...requestParamsObj.requestParams,
                    selectedFilterType: filterType,
                    label: option.label,
                    value: option.value
                }
            };
            if (option.label === 'All' && isSelectedFilterTypeTeamOrRm) {
                filtersAPI
                    .fetchFilteredValues(
                        generateRequestParamsForFilterApi(
                            tsStart ? tsStart.epoch : null,
                            tsEnd ? tsEnd.epoch : null,
                            zoneValue,
                            marketValue,
                            teamAllValue,
                            rmAllValue
                        ).requestParams
                    )
                    .then((data) => {
                        const enrichedParamsObjForFetchingWhenLabelIsAll = {
                            data,
                            filterType,
                            displayMapping: filtersDisplayMapping
                        };
                        // noinspection JSCheckFunctionSignatures
                        dispatch(
                            updateFiltersWhenAllIsSelectedForTeamsOrRms(
                                enrichedParamsObjForFetchingWhenLabelIsAll
                            )
                        );
                    })
                    .catch((error) =>
                        console.log('error fetching filters: ', error)
                    );
            } else if (
                option.label === 'All' &&
                (filterType === FilterType.MARKET ||
                    filterType === FilterType.ZONE)
            ) {
                if (filterType === FilterType.ZONE) {
                    dispatch(fetchFilteredDropdownValues(enrichedParamsObj));
                    resetFilters();
                } else {
                    resetFilters();
                }
            } else {
                dispatch(fetchFilteredDropdownValues(enrichedParamsObj));
            }

            setSearchFieldLength(0);
            onFiltersChanged && onFiltersChanged();
        }

        document.getElementById(`${filterName}Filter`).blur();
    };
    // Render method
    return (
        <div
            className={
                label.length >= 15
                    ? styles.longFilterWrapper
                    : styles.filterWrapper
            }
            style={style}
        >
            <label
                className={
                    label.length >= 15
                        ? styles.longFilterSearchContainer
                        : styles.filterSearchContainer
                }
            >
                <input
                    type="search"
                    id={`${filterName}Filter`}
                    className={styles.input}
                    value={searchFieldValue}
                    onFocus={() => {
                        setSearchFieldValue('');
                        setShowOptions(true);
                        setIsInFocus(true);
                    }}
                    onBlur={() => {
                        setSearchFieldValue('');
                        setShowOptions(false);
                        setIsInFocus(false);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Backspace' && searchFieldLength > 0) {
                            setSearchFieldLength(searchFieldLength - 1);
                        } else if (
                            e.key === 'Backspace' &&
                            searchFieldLength === 0
                        ) {
                            setSearchFieldLength(0);
                        } else setSearchFieldLength(searchFieldLength + 1);
                    }}
                    onChange={(e) => setSearchFieldValue(e.target.value)}
                />
                <span className={styles.placeholder}>
                    <div className={styles.leftContainer}>
                        {showFilterName && (
                            <p
                                className={
                                    isInFocus
                                        ? `${styles.focused} ${styles.first} ${
                                              searchFieldLength > 0 &&
                                              styles.invisible
                                          }`
                                        : styles.first
                                }
                            >
                                {filterName}
                            </p>
                        )}
                        <p
                            className={
                                isInFocus
                                    ? `${styles.focused} ${
                                          searchFieldLength > 0 &&
                                          styles.invisible
                                      }`
                                    : styles.second
                            }
                        >
                            {label}
                        </p>
                    </div>
                    <div className={styles.rightContainer}>
                        <img
                            src={dropdownArrow}
                            className={
                                isInFocus ? styles.invertedIcon : styles.icon
                            }
                            alt=""
                        />
                    </div>
                </span>
            </label>
            {showOptions && isInFocus && (
                <div className={styles.filterOptionsDropdownContainer}>
                    {processFilterOptions(options, searchFieldValue).map(
                        (option, idx) => (
                            <div
                                key={`option-${idx}`}
                                className={
                                    option.label === label
                                        ? styles.optionHighlightedText
                                        : styles.optionText
                                }
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={(e) => handleOptionOnClick(e, option)}
                            >
                                {option.label}
                            </div>
                        )
                    )}
                </div>
            )}
        </div>
    );
};

FilterSearch.propTypes = {
    style: PropTypes.object,
    filterName: PropTypes.oneOf([
        'Region',
        'Market',
        'Team',
        'CTO Code',
        'User Profile Region'
    ]),
    filterType: PropTypes.oneOf(['zone', 'market', 'team', 'rm']),
    searchFieldValue: PropTypes.string.isRequired,
    setSearchFieldValue: PropTypes.func.isRequired,
    searchFieldLength: PropTypes.number.isRequired,
    setSearchFieldLength: PropTypes.func.isRequired,
    showOptions: PropTypes.bool.isRequired,
    setShowOptions: PropTypes.func.isRequired,
    isInFocus: PropTypes.bool.isRequired,
    setIsInFocus: PropTypes.func.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.number,
    options: PropTypes.array,
    onFiltersChanged: PropTypes.func,
    resetFilters: PropTypes.func,
    showFilterName: PropTypes.bool
};

export default FilterSearch;
